import React, { useEffect, useState } from "react";
import EmrVisitsService from "../../../../services/EmrVisitsService";
import { capitalizeName, isSet } from "../../../../utils/commonUtils";
import TableGrid from "../../../../components/UI/TableGrid";
import WorkRestrictionsModal from "../WorkRestrictionsModal";
import FileManagerService from "../../../../services/FileManagerService";
import PERMISSIONS from "../../../../utils/AclPermissions";

const WorkStatusPage = (props) => {
  const { visit_id, Loader, visitDetails, employeeDetails, employeeId, fetchEmployeeDetails } = props;
  const visitDetailsNorm = Array.isArray(visitDetails) ? visitDetails[0] : visitDetails;
  const employerEmail = visitDetailsNorm?.company?.general_email;
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  const [workStatusFormData, setWorkStatusFormData] = useState(null);

  const tableHeaders = [
    { label: "Patient Name", key: "name" },
    { label: "Visit Date", key: "visit_date" },
    { label: "Visit Category", key: "visit_category" },
    { label: "Actions", key: "action", type: "Actions" },
  ];

  const [tableRecords, setTableRecords] = useState({
    tableHeaders: tableHeaders,
    tableRows: {
      data: [],
      status: 1000,
    },
    pagination: {
      page: 1,
      perPage: 25,
      totalRecords: 0,
      sort_by: "date",
      sort_order: "asc",
      search: {},
    },
  });

  useEffect(() => {
    if (visit_id) {
      fetchWorkStatusForm();
    }
  }, [visit_id]);

  const fetchWorkStatusForm = async () => {
    if (!visit_id) return;

    try {
      // setLoading(true);
       Loader(true);

      const data = await EmrVisitsService.getWorkStatusFormWithMapping(visit_id);

      if (data && !data.error) {
        // Combine current and mapped work status forms into a single array
        // Current form first, then mapped forms
        const allForms = [];
        if (data.current_work_status) {
          allForms.push({ ...data.current_work_status, isCurrentVisit: true });
        }
        if (data.mapped_work_status && Array.isArray(data.mapped_work_status)) {
          allForms.push(...data.mapped_work_status.map(form => ({ ...form, isCurrentVisit: false })));
        }

        // Map the response data to table rows
        const tableData = allForms.map((item) => {
          const employeeName = item.employee
            ? `${item.employee.first_name || ""} ${item.employee.last_name || ""}`.trim()
            : "-";
          // Only show current visit indicator if current_work_status exists and this is the current visit
          const isCurrentVisit = item.isCurrentVisit === true;

          return {
            rowId: item.work_status_form_id,
            rowClassName: isCurrentVisit ? 'current-visit-row' : '',
            data: {
              name: {
                value: (
                  <div className="d-flex align-items-center">
                    <a className="forTable_textEllipsis" title={employeeName}>
                      {employeeName || "-"}
                    </a>
                    {isCurrentVisit && (
                      <span className="badge badge-primary ml-2" style={{ fontSize: '0.75rem', backgroundColor: '#007bff', color: 'white' }}>
                        Current Visit
                      </span>
                    )}
                  </div>
                ),
              },
              visit_date: {
                value: (
                  <a className="forTable_textEllipsis" title={item.visit_date || "-"}>
                    {item.visit_date || "-"}
                  </a>
                ),
              },
              visit_category: {
                value: (
                  <a className="forTable_textEllipsis" title={item.visit_category || "-"}>
                    {item.visit_category ? capitalizeName(item.visit_category) : "-"}
                  </a>
                ),
              },
              action: [
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Search",
                  title: "Edit",
                  acl: PERMISSIONS.WORK_STATUS.EDIT,
                  clickHandler: () => handleEdit(item),
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Print",
                  title: "Print",
                  acl: PERMISSIONS.WORK_STATUS.LIST,
                  clickHandler: () => handlePrint(item),
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Email",
                  iconSize: 18,
                  iconColor: item.email_sent ? '#28a745' : null,
                  title: "Send Email",
                  acl: PERMISSIONS.WORK_STATUS.EDIT,
                  iconDropDownList: [
                    { label: "Send to Patient", clickHandler: () => handleMail(item, "patient") },
                    { label: "Send to Employer", clickHandler: () => handleMail(item, "employer") },
                  ],
                },
                {
                  className: "btn btn-icon btn-transparent-dark",
                  iconType: "Phone",
                  iconSize: 18,
                  iconColor: item.sms_sent ? '#28a745' : null,
                  title: "Send SMS",
                  acl: PERMISSIONS.WORK_STATUS.EDIT,
                  clickHandler: () => handleSms(item),
                },
              ],
            },
          };
        });

        setTableRecords((prevState) => ({
          ...prevState,
          tableRows: {
            data: tableData,
            status: 200,
          },
          pagination: {
            ...prevState.pagination,
            totalRecords: allForms.length,
          },
        }));

        console.log("Work Status Form Data:", data);
      } else {
        console.error("Error fetching work status form:", data?.error || "Unknown error");
        if (props.Toast) {
          props.Toast.error(data?.message || "Failed to fetch work status form");
        }
        // Set empty data on error
        setTableRecords((prevState) => ({
          ...prevState,
          tableRows: {
            data: [],
            status: 200,
          },
          pagination: {
            ...prevState.pagination,
            totalRecords: 0,
          },
        }));
      }
    } catch (error) {
      console.error("Error fetching work status form:", error);
      if (props.Toast) {
        props.Toast.error("Failed to fetch work status form");
      }
      // Set empty data on error
      setTableRecords((prevState) => ({
        ...prevState,
        tableRows: {
          data: [],
          status: 200,
        },
        pagination: {
          ...prevState.pagination,
          totalRecords: 0,
        },
      }));
    } finally {
      // setLoading(false);
      Loader(false);
    }
  };

  // Handle edit - fetch work status form and open modal
  const handleEdit = async (item) => {
    try {
      if (Loader) Loader(true);
      
      // Call API to get work status form data
      const formData = await EmrVisitsService.getWorkStatusFormById(item.visit_id);
      
      if (formData && !formData.error) {
        setWorkStatusFormData(formData);
        setSelectedVisitId(item.visit_id);
        setIsModalOpen(true);
      } else {
        if (props.Toast) {
          props.Toast.error("Failed to fetch work status form");
        }
      }
    } catch (error) {
      console.error("Error fetching work status form:", error);
      if (props.Toast) {
        props.Toast.error("Failed to fetch work status form");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedVisitId(null);
    setWorkStatusFormData(null);
    // Refresh the table data after closing modal
    if (visit_id) {
      fetchWorkStatusForm();
    }
  };

  const handlePrint = async (item) => {
    try {
      if (Loader) Loader(true);
      
      const response = await EmrVisitsService.printWorkStatusForm(item.visit_id);
      console.log("Print response:", response);
      
      // Handle different response structures
      const base64Data = typeof response === 'string' ? response : (response?.data || response);
      
      if (base64Data && typeof base64Data === 'string' && base64Data.length > 0) {
        const pdfData = {
          file_content: base64Data,
          original_name: `snap work status report`,
          mime_type: 'application/pdf'
        };
        FileManagerService.printPdf(pdfData);
      } else {
        console.error("Invalid PDF data received:", base64Data);
        if (props.Toast) {
          props.Toast.error("Failed to generate work status form PDF");
        }
      }
    } catch (error) {
      console.error("Error printing work status form:", error);
      if (props.Toast) {
        props.Toast.error("Failed to print work status form");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handleMail = async (item, recipientType = "patient") => {
    try {
      if (Loader) Loader(true);

      const employeeEmail = item?.employee?.email || employeeDetails?.email;
      const employerEmailAddress = employerEmail; // from current visit's company

      const email = recipientType === "employer" ? employerEmailAddress : employeeEmail;
      const recipientLabel = recipientType === "employer" ? "employer" : "patient";

      if (!email) {
        if (props.Toast) {
          props.Toast.error(
            recipientType === "employer" 
              ? "Employer email not found. Cannot send email."
              : "Employee email not found. Cannot send email."
          );
        }
        if (Loader) Loader(false);
        return;
      }

      const response = await EmrVisitsService.sendWorkStatusFormEmail(item.visit_id, email, recipientType);

      if (response && (response.code === 200 || response.code === 201)) {
        if (props.Toast) {
          props.Toast.success(`Work status report sent successfully to ${recipientLabel} (${email})`);
        }

        const employeeIdToSave = item.employee_id || item?.employee?.employee_id || employeeId;
        if (employeeIdToSave) {
          const savePayload = {
            employee_id: employeeIdToSave,
            email_sent: true,
            sms_sent: item.sms_sent || false,
          };

          await EmrVisitsService.saveWorkStatusForm(item.visit_id, savePayload);

          if (visit_id) {
            fetchWorkStatusForm();
          }
        }
      } else {
        if (props.Toast) {
          props.Toast.error(response?.data?.error || response?.message || "Failed to send work status report email");
        }
      }
    } catch (error) {
      console.error("Error sending work status form email:", error);
      if (props.Toast) {
        props.Toast.error("Failed to send work status report email");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  const handleSms = async (item) => {
    try {
      if (Loader) Loader(true);

      // Get employee phone from item or employeeDetails
      const employeePhone = item?.employee?.phone || employeeDetails?.phone;
      
      if (!employeePhone) {
        if (props.Toast) {
          props.Toast.error("Employee phone number not found. Cannot send SMS.");
        }
        return;
      }

      const response = await EmrVisitsService.sendWorkStatusFormSMS(item.visit_id, employeePhone);
      
      if (response && (response.code === 200 || response.code === 201)) {
        if (props.Toast) {
          props.Toast.success("Work status report SMS sent successfully to " + employeePhone);
        }
        
        // Update sms_sent flag by calling save API
        const employeeIdToSave = item.employee_id || item?.employee?.employee_id || employeeId;
        if (employeeIdToSave) {
          const savePayload = {
            employee_id: employeeIdToSave,
            email_sent: item.email_sent || false,
            sms_sent: true,
          };
          
          await EmrVisitsService.saveWorkStatusForm(item.visit_id, savePayload);
          
          // Refresh the list after saving
          if (visit_id) {
            fetchWorkStatusForm();
          }
        }
      } else {
        if (props.Toast) {
          props.Toast.error(response?.data?.error || response?.message || "Failed to send work status report SMS");
        }
      }
    } catch (error) {
      console.error("Error sending work status form SMS:", error);
      if (props.Toast) {
        props.Toast.error("Failed to send work status report SMS");
      }
    } finally {
      if (Loader) Loader(false);
    }
  };

  return (
    <div>
      <style>
        {`
          .current-visit-row {
            background-color: #e7f3ff !important;
            border-left: 4px solid #007bff !important;
          }
          .current-visit-row:hover {
            background-color: #d0e7ff !important;
          }
          .current-visit-row td {
            background-color: transparent !important;
          }
        `}
      </style>
      <div className="card sh_basecard cardhtmd">
        <div className="card-header p-2 sh_basecardHeader">
          <div className="d-flex w-100 justify-content-between align-items-center">
            Work Status List
          </div>
        </div>
        <div className="card-body p-0 my-2 px-0 sh_basecardBody">
          {loading && (
            <div className="text-center p-4">
              <div>Loading...</div>
            </div>
          )}
          {!loading && <TableGrid {...tableRecords} />}
        </div>
      </div>
      
      {isModalOpen && (
        <WorkRestrictionsModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          visit_id={selectedVisitId}
          employeeId={workStatusFormData?.employee_id || employeeId}
          employeeDetails={workStatusFormData?.employee || employeeDetails}
          fetchEmployeeDetails={fetchEmployeeDetails}
          visitDetails={visitDetails}
          Loader={Loader}
          workStatusFormData={workStatusFormData}
          Toast={props.Toast}
        />
      )}
    </div>
  );
};

export default WorkStatusPage;

